import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { formatName } from "../../../services/data_requests";
import "./LatestReleases.scss";
import { logo } from "../../../assets";

const LatestReleases = ({ title, subtitle, apiURL = "", count = 7 }) => {
  const [items, setItems] = useState([]);
  const containerRef = useRef(null);
  const sectionRef = useRef(null);
  const [currentCursor, setCurrentCursor] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  const scrollRaf = useRef(null);
  const cursorRef = useRef(0);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "200px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const onScroll = () => {
      if (scrollRaf.current) return;

      scrollRaf.current = requestAnimationFrame(() => {
        const { scrollLeft, scrollWidth, clientWidth } = container;
        const scrollPercentage = scrollLeft / (scrollWidth - clientWidth);

        const nextCursor = scrollPercentage > 0.5 ? 1 : 0;

        if (cursorRef.current !== nextCursor) {
          cursorRef.current = nextCursor;
          setCurrentCursor(nextCursor);
        }

        scrollRaf.current = null;
      });
    };

    container.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      container.removeEventListener("scroll", onScroll);
      if (scrollRaf.current) cancelAnimationFrame(scrollRaf.current);
    };
  }, []);

  useEffect(() => {
    if (!apiURL || !isVisible) return;
    const fetchData = async () => {
      try {
        const res = await axios.get(apiURL);
        const sorted = res.data.sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        );
        setItems(sorted.slice(0, count));
      } catch (err) {
        console.error("LatestReleases error:", err);
      }
    };
    fetchData();
  }, [apiURL, count, isVisible]);

  const handleScroll = () => {
    if (containerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = containerRef.current;
      const scrollPercentage = scrollLeft / (scrollWidth - clientWidth);
      if (scrollPercentage > 0.5) setCurrentCursor(1);
      else setCurrentCursor(0);
    }
  };
  return (
    <section className="latest-releases" ref={sectionRef}>
      <div className="latest-releases__header">
        <h2>{title}</h2>
        {subtitle && <p>{subtitle}</p>}
      </div>

      <div
        className="latest-releases__list"
        ref={containerRef}
        onScroll={handleScroll}
      >
        {items.length === 0 ? (
          <p>No artwork available</p>
        ) : (
          items.map((item, index) => {
            const hasImage =
              item.prev_img_url && item.prev_img_url.trim() !== "";

            return (
              <Link
                to={`/artwork/${formatName(item.name)}-${item.id}`}
                key={item.id || index}
                className={`latest-releases__card ${
                  !hasImage ? "latest-releases__card--no-image" : ""
                }`}
              >
                <img
                  src={hasImage ? item.prev_img_url : logo}
                  alt={item.name || `Artwork ${index + 1}`}
                  className={
                    hasImage
                      ? "latest-releases__image"
                      : "latest-releases__image latest-releases__image--placeholder"
                  }
                  loading={index < 2 ? "eager" : "lazy"}
                  decoding="async"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = logo;
                    e.target.classList.add(
                      "latest-releases__image--placeholder"
                    );
                  }}
                />
              </Link>
            );
          })
        )}
      </div>
    </section>
  );
};

export default LatestReleases;
